import * as CodeDaleTheme from "@CodeDaleTheme/global";
import { eventModal } from "@CodeDaleTheme/modal";
import { getQuizModal, initQuizSteps } from "./recommendation-quiz.js";

// Opens the shared quiz modal and initializes its steps. No-op if already open.
function openQuizPopup() {
  const modal = getQuizModal();
  const quizPopup = document.querySelector("quiz-popup[data-enable]");
  if (!modal || modal.classList.contains("active")) return;
  eventModal(modal, "open", true, null, true);
  CodeDaleTheme.global.rootToFocus = modal;
  quizPopup?.initNotShow(modal);
  initQuizSteps(modal);
}

if (!customElements.get("quiz-fixed-image")) {
  customElements.define(
    "quiz-fixed-image",
    class QuizFixedImage extends HTMLElement {
      constructor() {
        super();
        this.image = null;
        this.hasAnimated = false;
        this.settledTransform = "translateX(-10%)";
        this.easing = [0.22, 0.61, 0.36, 1];
      }

      connectedCallback() {
        this.image = this.querySelector(".quiz__fixed-image-img");
        if (!this.image) return;

        this.addEventListener("click", this.handleClick.bind(this));
        this.addEventListener("keydown", this.handleKeydown.bind(this));

        if (this.shouldDisableAnimation()) {
          this.image.style.transform = this.settledTransform;
          return;
        }

        this.image.style.transform = "translateX(-30%)";
        this.setupHover();
        this.setupInView();
      }

      handleClick() {
        this.initPopup();
      }

      handleKeydown(event) {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.initPopup();
        }
      }

      initPopup() {
        openQuizPopup();
      }

      shouldDisableAnimation() {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          return true;
        }

        const scrollAnimation =
          window.themeGlobalVariables?.settings?.scrollAnimation;

        if (scrollAnimation === "disable") {
          return true;
        }

        if (scrollAnimation === "desktop_only" && window.innerWidth < 768) {
          return true;
        }

        return false;
      }

      isInViewport() {
        const rect = this.getBoundingClientRect();

        return (
          rect.width > 0 &&
          rect.height > 0 &&
          rect.bottom > 0 &&
          rect.right > 0 &&
          rect.top < window.innerHeight &&
          rect.left < window.innerWidth
        );
      }

      playEntranceOnce() {
        if (this.hasAnimated) return;
        this.hasAnimated = true;
        this.playEntrance();
      }

      // Resolves once the Motion library is available, or after maxWait ms.
      waitForMotion(maxWait = 3000) {
        return new Promise((resolve) => {
          if (typeof Motion !== "undefined") {
            resolve();
            return;
          }

          const startedAt = Date.now();
          const pollTimerId = setInterval(() => {
            if (
              typeof Motion !== "undefined" ||
              Date.now() - startedAt >= maxWait
            ) {
              clearInterval(pollTimerId);
              resolve();
            }
          }, 50);
        });
      }

      waitForImage() {
        return new Promise((resolve) => {
          if (!this.image) {
            resolve();
            return;
          }

          if (this.image.complete && this.image.naturalWidth > 0) {
            resolve();
            return;
          }

          this.image.addEventListener("load", resolve, { once: true });
          this.image.addEventListener("error", resolve, { once: true });
        });
      }

      async setupInView() {
        await this.waitForMotion();
        await this.waitForImage();

        const startAnimation = () => {
          setTimeout(() => this.playEntranceOnce(), 100);
        };

        // Fixed images stay in the viewport — animate on load, not page-bottom scroll.
        if (this.isInViewport()) {
          startAnimation();
          return;
        }

        if (typeof Motion === "undefined") {
          this.playEntranceOnce();
          return;
        }

        Motion.inView(
          this,
          () => this.playEntranceOnce(),
          { margin: "0px 0px -30px 0px" },
        );
      }

      playEntrance() {
        if (typeof Motion === "undefined") {
          this.image.style.transform = this.settledTransform;
          return;
        }

        Motion.animate(
          this.image,
          { transform: this.settledTransform },
          {
            duration: 0.6,
            easing: this.easing,
          },
        );
      }

      setupHover() {
        this.addEventListener("mouseenter", this.onMouseEnter);
        this.addEventListener("mouseleave", this.onMouseLeave);
      }

      onMouseEnter() {
        if (typeof Motion === "undefined") {
          this.image.style.transform = "translateX(0%)";
          return;
        }

        Motion.animate(
          this.image,
          { transform: "translateX(0%)" },
          { duration: 0.3, easing: this.easing },
        );
      }

      onMouseLeave() {
        if (typeof Motion === "undefined") {
          this.image.style.transform = this.settledTransform;
          return;
        }

        Motion.animate(
          this.image,
          { transform: this.settledTransform },
          { duration: 0.3, easing: this.easing },
        );
      }

      disconnectedCallback() {
        this.removeEventListener("mouseenter", this.onMouseEnter);
        this.removeEventListener("mouseleave", this.onMouseLeave);
      }
    },
  );
}

document.addEventListener("click", (event) => {
  const trigger = event.target.closest("[data-quiz-trigger]");
  if (trigger) openQuizPopup();
});